import React, { useEffect, useRef, useState } from 'react'
import { Router, useLocation } from '@reach/router'
import axios from 'axios'
import SEO from '../components/seo'
import NotFound from './404'
import { baseURL, WORKER_BASE_URL } from '../constants'
import RegisterToEarnCashbackModal from '../components/registerToEarnCashbackModal'
import ArticleCard, {
  ArticleCardData,
  articleLabel,
} from '../components/contentHub/ArticleCard'
import { ImageCrop, imageCropStyles } from '../components/contentHub/imageCrop'
import JsonLdSchema from '../components/jsonLdSchema'
import {
  PageShell,
  Loading,
  CullectorFonts,
  CullectorHeader,
  CullectorFooter,
  NewsletterBand,
  CategoryRail,
  getActiveEditorialCategories,
  CULLECTOR_SEARCH_PATH,
} from '../components/contentHub/hubShared'

const siteUrl = process.env.GATSBY_SITE_URL || 'https://chirpyest.com'
const hubUrl = `${siteUrl}/cullector`

interface RetailerInfo {
  brandId: string
  brandName: string
  commission: string
  hasExceptions: boolean
}

interface ContentPage {
  title: string
  metaDescription?: string
  topic?: string
  htmlContent: string
  structuredData?: object | object[]
  status: string
  featureImage?: string
  featureImageCrop?: ImageCrop | null
  categoryName?: string
  editorialCategory?: { name: string; slug: string } | null
  editorialSubcategory?: { name: string; slug: string } | null
  author?: string
  retailerInfo?: RetailerInfo[]
  cashbackEnabled?: boolean
  publishedAt?: string | null
  updatedAt?: string | null
}

// "August 10, 2026" — day-level date for the article's published/updated line.
const fullDate = (dateString?: string | null): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

// True when the row was updated on a later calendar day than it was published —
// updatedAt is bumped by any write, so a same-day touch never shows as an update.
const wasUpdatedAfterPublish = (page: ContentPage): boolean => {
  if (!page.publishedAt || !page.updatedAt) return false
  const published = new Date(page.publishedAt)
  const updated = new Date(page.updatedAt)
  if (Number.isNaN(published.getTime()) || Number.isNaN(updated.getTime()))
    return false
  return (
    updated.toDateString() !== published.toDateString() &&
    updated.getTime() > published.getTime()
  )
}

interface RelatedPage {
  id: number
  title: string
  slug: string
  featureImage?: string
  featureImageCrop?: ImageCrop | null
  excerpt?: string
  showExcerpt?: boolean
  author?: string
  publishedAt: string
  categoryName?: string
  editorialCategory?: { name: string; slug: string } | null
  editorialSubcategory?: { name: string; slug: string } | null
}

interface CullectorContentPageProps {
  slug?: string
  // Private draft-preview mode: fetch by secret share token instead of slug, render
  // regardless of status, with a preview banner and noindex — visually identical to
  // the published article otherwise.
  previewToken?: string
}

// External product/brand links open in a new tab; in-page anchors and internal
// root-relative links stay in the same tab. Applied to the fetched article body.
const addExternalLinkTargets = (html: string): string =>
  html.replace(/<a[^>]*>/gi, (match: string) => {
    if (/href=["']#/.test(match)) {
      return match
        .replace(/\s*target=["'][^"']*["']/gi, '')
        .replace(/\s*rel=["'][^"']*["']/gi, '')
    }
    if (/target=/i.test(match)) return match
    // Internal root-relative links (e.g. /cullector/..., /articles/...) stay in the same tab.
    if (/href=["']\//.test(match)) return match
    return match.replace('>', ' target="_blank" rel="noopener noreferrer">')
  })

// Origin + pathname only — no query string, so a preview token never leaks into
// worker/network logs. Gatsby builds this page in Node too, where there is no window.
const articlePageUrl = (): string =>
  typeof window === 'undefined'
    ? ''
    : `${window.location.origin}${window.location.pathname}`

// Outbound affiliate links are opened with rel="noopener noreferrer" (above) and via
// window.open(..., 'noreferrer') in the interstitial, so the browser sends no Referer to
// the go.chirpyest.com worker. Without it the worker records a null referrer and, for
// Awin, never sets its required extr= param. The worker accepts an explicit ?ref= as a
// fallback for exactly this case (the Chrome extension uses it for the same reason), so
// stamp the article URL onto every worker link in the article body.
//
// Append as a string: re-serialising via URLSearchParams would re-encode the `u` param
// and break the redirect — the same trap the backend's wrapWithWorkerUrl calls out.
const addWorkerReferrer = (html: string, pageUrl: string): string => {
  if (!pageUrl) return html

  let workerOrigin: string
  try {
    workerOrigin = new URL(WORKER_BASE_URL).origin
  } catch {
    return html
  }

  return html.replace(
    /href=(["'])(.*?)\1/gi,
    (match, quote: string, href: string) => {
      if (/[?&]ref=/.test(href)) return match
      try {
        if (new URL(href).origin !== workerOrigin) return match
      } catch {
        return match
      }
      return `href=${quote}${href}&ref=${encodeURIComponent(pageUrl)}${quote}`
    }
  )
}

const CullectorContentPage: React.FC<CullectorContentPageProps> = ({
  slug,
  previewToken,
}) => {
  const [contentPage, setContentPage] = useState<ContentPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [relatedPages, setRelatedPages] = useState<RelatedPage[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalBrandName, setModalBrandName] = useState('')
  const [modalCommission, setModalCommission] = useState('')
  const [modalHasExceptions, setModalHasExceptions] = useState(true)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (previewToken) {
      fetchPreviewPage(previewToken)
    } else if (slug) {
      fetchContentPage(slug)
    } else {
      setLoading(false)
    }
  }, [slug, previewToken])

  useEffect(() => {
    if (!contentPage || !contentRef.current) return
    if (contentPage.cashbackEnabled === false) return

    const brandById = new Map<string, RetailerInfo>()
    for (const r of contentPage.retailerInfo || []) {
      brandById.set(r.brandId, r)
    }

    const handleClick = (e: MouseEvent) => {
      let target = e.target as HTMLElement | null
      while (target && target.tagName !== 'A') {
        target = target.parentElement
      }
      if (!target || target.tagName !== 'A') return

      const anchor = target as HTMLAnchorElement
      if (!anchor.hasAttribute('data-brand-id')) return

      const href = anchor.getAttribute('href') || ''
      if (!href) return

      e.preventDefault()
      e.stopPropagation()

      const retailer =
        brandById.get(anchor.getAttribute('data-brand-id')!) ?? null

      setPendingUrl(href)
      setModalBrandName(retailer?.brandName ?? '')
      setModalCommission(retailer?.commission ?? '')
      setModalHasExceptions(retailer?.hasExceptions ?? true)
      setModalOpen(true)
    }

    const container = contentRef.current
    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [contentPage])

  const fetchRelatedPages = async (pageSlug: string) => {
    try {
      const response = await axios.get(
        `${baseURL}/api/v1/content-pages/public/${pageSlug}/related`
      )
      if (response.data && Array.isArray(response.data.data)) {
        setRelatedPages(response.data.data)
      }
    } catch {
      // silently ignore — related section is non-critical
    }
  }

  const fetchContentPage = async (pageSlug: string) => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${baseURL}/api/v1/content-pages/public/${pageSlug}`
      )

      if (response.data && response.data.data) {
        const page = response.data.data

        if (page.status === 'published') {
          fetchRelatedPages(pageSlug)
          page.htmlContent = addWorkerReferrer(
            addExternalLinkTargets(page.htmlContent),
            articlePageUrl()
          )
          setContentPage(page)
        }
      }
    } catch (err) {
      console.error('Error fetching content page:', err)
    } finally {
      setLoading(false)
    }
  }

  // Private draft preview: fetch by share token, render regardless of status.
  // Related articles are skipped — a draft has no public slug to relate from.
  const fetchPreviewPage = async (token: string) => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${baseURL}/api/v1/content-pages/preview/${token}`
      )
      if (response.data && response.data.data) {
        const page = response.data.data
        page.htmlContent = addWorkerReferrer(
          addExternalLinkTargets(page.htmlContent),
          articlePageUrl()
        )
        setContentPage(page)
      }
    } catch (err) {
      console.error('Error fetching preview page:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <>
        <CullectorFonts />
        <div className="cullector-hub">
          <CullectorHeader />
          <Loading />
        </div>
      </>
    )
  }

  if (!contentPage) {
    return (
      <>
        <CullectorFonts />
        <div className="cullector-hub">
          <CullectorHeader />
        </div>
        <NotFound />
      </>
    )
  }

  return (
    <>
      <SEO
        title={
          previewToken
            ? `Draft preview: ${contentPage.title}`
            : contentPage.title
        }
        description={contentPage.metaDescription || contentPage.topic}
        image={contentPage.featureImage}
        article
        noindex={!!previewToken}
      />
      {!previewToken && contentPage.structuredData && (
        <JsonLdSchema
          schema={contentPage.structuredData}
          keyOverride="article-jsonld"
        />
      )}
      {previewToken && (
        <div
          style={{
            background: '#1A1A1A',
            color: '#F7F4EE',
            textAlign: 'center',
            padding: '10px 16px',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {contentPage.status === 'published'
            ? 'Preview link — this article is now live.'
            : 'Draft preview — not published yet. Please don’t share this link outside the team.'}
        </div>
      )}
      <RegisterToEarnCashbackModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setPendingUrl(null)
        }}
        onContinue={() => {
          setModalOpen(false)
          if (pendingUrl) {
            window.open(pendingUrl, '_blank', 'noopener,noreferrer')
          }
          setPendingUrl(null)
        }}
        brandName={modalBrandName}
        commission={modalCommission}
        hasExceptions={modalHasExceptions}
      />
      <CullectorFonts />
      <div className="cullector-hub">
        <CullectorHeader activeSlug={contentPage.editorialCategory?.slug} />
      </div>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* FTC affiliate disclosure — shown on every Cullector page/article, above the fold.
            10px is the one sanctioned exception to the 12px type floor. */}
        <p
          style={{
            margin: '0 0 16px',
            textAlign: 'center',
            color: '#767676',
            fontFamily: "'DM Sans', system-ui, sans-serif",
            fontWeight: 300,
            fontSize: 10,
            letterSpacing: '0.02em',
            opacity: 0.75,
          }}
        >
          We make money from links on our site.
        </p>
        {contentPage.featureImage && (
          <div style={{ overflow: 'hidden' }}>
            <img
              src={contentPage.featureImage}
              alt={contentPage.title}
              style={{
                width: '100%',
                // Render at the og-image ratio feature images are composed at
                // (1200x630) instead of capping height — a cap turned the hero into
                // a wider window that cover-cropped ~20% off the composed image.
                // Browsers without aspect-ratio fall back to natural height.
                aspectRatio: '1200 / 630',
                objectFit: 'cover',
                display: 'block',
                ...imageCropStyles(contentPage.featureImageCrop),
              }}
            />
          </div>
        )}
        {(() => {
          const categoryLabel =
            contentPage.editorialSubcategory?.name ||
            contentPage.editorialCategory?.name ||
            contentPage.categoryName
          const publishedLabel = fullDate(contentPage.publishedAt)
          const updatedLabel = wasUpdatedAfterPublish(contentPage)
            ? fullDate(contentPage.updatedAt)
            : ''
          return categoryLabel || contentPage.author || publishedLabel ? (
            <div
              style={{ textAlign: 'center', marginTop: 32, marginBottom: -32 }}
            >
              {categoryLabel && (
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    color: '#3D3D3D',
                  }}
                >
                  {categoryLabel}
                </span>
              )}
              {contentPage.author && (
                <span
                  style={{
                    display: 'block',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: '#767676',
                    marginTop: 6,
                  }}
                >
                  by {contentPage.author}
                </span>
              )}
              {publishedLabel && (
                <span
                  style={{
                    display: 'block',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    color: '#767676',
                    marginTop: 6,
                  }}
                >
                  Published on {publishedLabel}
                  {/* The original publication date stays visible; a later edit adds
                    the updated date alongside it. */}
                  {updatedLabel && <> · Updated on {updatedLabel}</>}
                </span>
              )}
            </div>
          ) : null
        })()}
        <div
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: contentPage.htmlContent }}
        />
        {contentPage.cashbackEnabled !== false && (
          <div
            style={{
              maxWidth: 960,
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'center',
              padding: '48px 20px',
            }}
          >
            <a
              href="/join/contentnetwork"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 54,
                maxWidth: 460,
                width: '100%',
                margin: '0 auto',
                border: '1px solid #1A1A1A',
                borderRadius: 3,
                background: '#1A1A1A',
                textDecoration: 'none',
                color: '#FFFFFF',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                fontWeight: 400,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                boxSizing: 'border-box',
              }}
            >
              Join Chirpyest to earn cash back
            </a>
          </div>
        )}
        <p
          style={{
            textAlign: 'center',
            color: '#767676',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 300,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          Content contains affiliate links, which means the site receives a
          commission from any purchases made from recommendations.
        </p>
      </div>
      <div className="cullector-hub">
        {relatedPages.length > 0 && (
          <section className="section" aria-label="Related guides">
            <div className="wrap">
              <div className="section-head">
                <h2 className="t-h2">You might also like</h2>
              </div>
              <div className="story-grid">
                {relatedPages.map(page => (
                  <ArticleCard
                    key={page.id}
                    article={{
                      id: page.id,
                      title: page.title,
                      slug: page.slug,
                      featureImage: page.featureImage,
                      featureImageCrop: page.featureImageCrop,
                      excerpt: page.excerpt,
                      showExcerpt: page.showExcerpt,
                      author: page.author,
                      publishedAt: page.publishedAt,
                      category:
                        page.editorialCategory ||
                        (page.categoryName
                          ? { name: page.categoryName, slug: '' }
                          : null),
                      subcategory: page.editorialSubcategory || null,
                    }}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
        <NewsletterBand />
        <CullectorFooter />
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Cullector landing page: admin-configured sections (featured / latest / category)
// rendered in the handoff layout — the first featured + first latest sections
// combine into the hero/latest top band; every other section renders in the
// order the backend returns it.
// ---------------------------------------------------------------------------

interface LandingCategoryRef {
  id: number
  name: string
  slug: string
}

interface LandingSection {
  type: 'featured' | 'latest' | 'category'
  hero?: ArticleCardData | null
  articles: ArticleCardData[]
  category?: LandingCategoryRef
}

const monthYear = (dateString?: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const LatestRow: React.FC<{ article: ArticleCardData }> = ({ article }) => {
  const label = articleLabel(article)
  const href = `/cullector/${article.slug}`
  return (
    <div className="latest-row">
      <a className="latest-thumb" href={href} tabIndex={-1} aria-hidden="true">
        {article.featureImage && (
          <img
            src={article.featureImage}
            alt=""
            loading="lazy"
            style={imageCropStyles(article.featureImageCrop)}
          />
        )}
      </a>
      <div className="latest-body">
        {label && <span className="t-eyebrow">{label}</span>}
        <h3 className="t-h2">
          <a href={href}>{article.title}</a>
        </h3>
        {article.showExcerpt && article.excerpt && (
          <p className="story-dek t-body">{article.excerpt}</p>
        )}
      </div>
    </div>
  )
}

const HeroBlock: React.FC<{
  hero: ArticleCardData
  secondaries: ArticleCardData[]
}> = ({ hero, secondaries }) => {
  const href = `/cullector/${hero.slug}`
  const byline = [
    hero.author ? `By ${hero.author}` : '',
    monthYear(hero.publishedAt),
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="hero">
      <h1 className="t-h1">
        <a href={href}>{hero.title}</a>
      </h1>
      {hero.featureImage ? (
        <a
          className="photo-block has-image"
          href={href}
          tabIndex={-1}
          aria-hidden="true"
        >
          <img
            src={hero.featureImage}
            alt={hero.title}
            style={imageCropStyles(hero.featureImageCrop)}
          />
        </a>
      ) : (
        <a
          className="photo-block"
          href={href}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
      {hero.excerpt && <p className="dek t-body">{hero.excerpt}</p>}
      {byline && <p className="hero-byline t-caption">{byline}</p>}
      {secondaries.map(article => {
        const label = articleLabel(article)
        return (
          <div className="hero-secondary" key={article.id}>
            {label && <span className="t-eyebrow">{label}</span>}
            <h3 className="t-h2">
              <a href={`/cullector/${article.slug}`}>{article.title}</a>
            </h3>
            {article.showExcerpt && article.excerpt && (
              <p className="story-dek t-body">{article.excerpt}</p>
            )}
            {article.author && (
              <span className="story-byline">{article.author}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

const GridSection: React.FC<{
  title: string
  articles: ArticleCardData[]
  id?: string
  viewAllHref?: string
  viewAllLabel?: string
}> = ({ title, articles, id, viewAllHref, viewAllLabel }) => (
  <section className="section" id={id} aria-label={title}>
    <div className="wrap">
      <div className="section-head">
        <h2 className="t-h2">{title}</h2>
        {viewAllHref && (
          <a className="view-all" href={viewAllHref}>
            {viewAllLabel || 'View all'} →
          </a>
        )}
      </div>
      <div className="story-grid">
        {articles.map(article => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>
    </div>
  </section>
)

const homeJsonLd = (categoryNames: string[]) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${hubUrl}#organization`,
      name: 'Cullector',
      url: hubUrl,
      slogan: 'The best, curated.',
      parentOrganization: {
        '@type': 'Organization',
        name: 'Chirpyest',
        url: siteUrl,
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${hubUrl}#website`,
      url: hubUrl,
      name: 'Cullector',
      description: 'Product reviews and buying guides, tested and compared.',
      publisher: { '@id': `${hubUrl}#organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}${CULLECTOR_SEARCH_PATH}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Chirpyest', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Cullector', item: hubUrl },
      ],
    },
    {
      '@type': 'CollectionPage',
      '@id': `${hubUrl}#collection`,
      url: hubUrl,
      name: 'Cullector Buying Guides',
      isPartOf: { '@id': `${hubUrl}#website` },
      ...(categoryNames.length > 0 ? { about: categoryNames } : {}),
    },
  ],
})

const CullectorHome: React.FC<{ path?: string }> = () => {
  const [sections, setSections] = useState<LandingSection[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios
      .get(`${baseURL}/api/v1/content-pages/public/landing`)
      .then(res => setSections(res.data?.data?.sections || []))
      .catch(() => setSections([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <PageShell title="Cullector — The Best, Curated">
        <Loading />
      </PageShell>
    )
  }

  const featuredIndex = sections.findIndex(s => s.type === 'featured')
  const latestIndex = sections.findIndex(s => s.type === 'latest')
  const featured = featuredIndex >= 0 ? sections[featuredIndex] : null
  const latest = latestIndex >= 0 ? sections[latestIndex] : null
  const rest = sections.filter(
    (_, i) => i !== featuredIndex && i !== latestIndex
  )

  const heroArticle = featured?.hero || featured?.articles?.[0] || null
  const nonHeroFeatured = featured
    ? featured.articles.filter(a => a.id !== heroArticle?.id)
    : []
  // The hero block carries one secondary story (per the handoff); any further
  // featured articles get their own grid section so admin curation isn't dropped.
  const heroSecondaries = nonHeroFeatured.slice(0, 1)
  const extraFeatured = nonHeroFeatured.slice(1)
  const latestRows = latest?.articles.slice(0, 4) || []

  const categoryNames = rest
    .filter(s => s.type === 'category' && s.category)
    .map(s => (s.category as LandingCategoryRef).name)

  return (
    <PageShell
      title="Cullector — The Best, Curated"
      description="Cullector tests and compares products across categories, then tells you exactly which ones are worth buying. No fluff, just the pick."
      belowNewsletter={<CategoryRail />}
    >
      <JsonLdSchema
        schema={homeJsonLd(categoryNames)}
        keyOverride="cullector-home-jsonld"
      />
      {(heroArticle || latestRows.length > 0) && (
        <section className="top-band" aria-label="Featured and latest guides">
          <div
            className={`wrap top-band-grid${
              !heroArticle || latestRows.length === 0 ? ' single-col' : ''
            }`}
          >
            {heroArticle && (
              <HeroBlock hero={heroArticle} secondaries={heroSecondaries} />
            )}
            {latestRows.length > 0 && (
              <div className="latest-col">
                <div className="latest-head">
                  <h2 className="t-h2">Latest</h2>
                </div>
                {latestRows.map(article => (
                  <LatestRow key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
          <div className="wrap disclosure-line">
            <span>We make money from links on our site.</span>
          </div>
        </section>
      )}
      {extraFeatured.length > 0 && (
        <GridSection title="Featured" articles={extraFeatured} />
      )}
      {rest.map(section => {
        if (section.type === 'category' && section.category) {
          const category = section.category
          return (
            <GridSection
              key={`category-${category.id}`}
              id={category.slug}
              title={category.name}
              articles={section.articles}
              viewAllHref={`/cullector/${category.slug}`}
              viewAllLabel={`View all ${category.name.toLowerCase()} guides`}
            />
          )
        }
        // Any additional featured/latest sections beyond the top band render as
        // plain grids in their configured order.
        if (section.articles.length > 0) {
          return (
            <GridSection
              key={section.type}
              title={section.type === 'latest' ? 'Latest' : 'Featured'}
              articles={section.articles}
            />
          )
        }
        return null
      })}
      {sections.length === 0 && (
        <div className="hub-empty t-body">
          No guides published yet. Check back soon.
        </div>
      )}
    </PageShell>
  )
}

// ---------------------------------------------------------------------------
// Category / subcategory landing pages: /cullector/<cat> and /cullector/<cat>/<sub>
// with numbered pagination via ?page=N (per the handoff category template).
// ---------------------------------------------------------------------------

const CATEGORY_PAGE_SIZE = 24

interface CategoryLandingData {
  category: {
    id: number
    name: string
    slug: string
    introParagraph: string | null
    parent: any
  }
  subcategories: { id: number; name: string; slug: string }[]
  articles: ArticleCardData[]
  articlesTotal: number
}

const paginationItems = (
  page: number,
  totalPages: number
): (number | '…')[] => {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  const wanted = [...new Set([1, page - 1, page, page + 1, totalPages])]
    .filter(p => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b)
  const items: (number | '…')[] = []
  let prev = 0
  for (const p of wanted) {
    if (p - prev > 1) items.push('…')
    items.push(p)
    prev = p
  }
  return items
}

const Pagination: React.FC<{
  page: number
  totalPages: number
  basePath: string
  // Override when the page carries other query params (e.g. search ?q=) that the
  // default `basePath?page=N` would drop.
  pageHref?: (n: number) => string
}> = ({ page, totalPages, basePath, pageHref: customPageHref }) => {
  const pageHref =
    customPageHref ||
    ((n: number) => (n === 1 ? basePath : `${basePath}?page=${n}`))
  return (
    <nav className="pagination" aria-label="Pagination">
      {paginationItems(page, totalPages).map((item, index) =>
        item === '…' ? (
          // eslint-disable-next-line react/no-array-index-key
          <span className="ellipsis" key={`ellipsis-${index}`}>
            …
          </span>
        ) : item === page ? (
          <span className="current" key={item} aria-current="page">
            {item}
          </span>
        ) : (
          <a key={item} href={pageHref(item)}>
            {item}
          </a>
        )
      )}
      {page < totalPages && (
        <a className="next" href={pageHref(page + 1)}>
          Next →
        </a>
      )}
    </nav>
  )
}

const categoryJsonLd = (
  name: string,
  path: string,
  parent?: { name: string; slug: string } | null
) => {
  const crumbs = [
    { '@type': 'ListItem', position: 1, name: 'Cullector', item: hubUrl },
  ]
  if (parent) {
    crumbs.push({
      '@type': 'ListItem',
      position: 2,
      name: parent.name,
      item: `${hubUrl}/${parent.slug}`,
    })
  }
  crumbs.push({
    '@type': 'ListItem',
    position: crumbs.length + 1,
    name,
    item: `${siteUrl}${path}`,
  })
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'BreadcrumbList', itemListElement: crumbs },
      {
        '@type': 'CollectionPage',
        url: `${siteUrl}${path}`,
        name: `${name} Guides`,
        isPartOf: { '@id': `${hubUrl}#website` },
      },
    ],
  }
}

const CategoryLanding: React.FC<{
  path?: string
  categorySlug?: string
  subcategorySlug?: string
}> = ({ categorySlug, subcategorySlug }) => {
  const slug = subcategorySlug || categorySlug
  const location = useLocation()
  const page = Math.max(
    1,
    parseInt(new URLSearchParams(location.search).get('page') || '1', 10) || 1
  )
  const [data, setData] = useState<CategoryLandingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const hasLoadedOnce = useRef(false)

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      return
    }
    setLoading(true)
    setNotFound(false)
    axios
      .get(
        `${baseURL}/api/v1/content-pages/public/category/${slug}?limit=${CATEGORY_PAGE_SIZE}&offset=${(page -
          1) *
          CATEGORY_PAGE_SIZE}`
      )
      .then(res => {
        setData(res.data?.data || null)
        if (hasLoadedOnce.current) window.scrollTo(0, 0)
        hasLoadedOnce.current = true
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [slug, page])

  if (loading) {
    return (
      <PageShell title="Cullector — The Best, Curated">
        <Loading />
      </PageShell>
    )
  }
  if (notFound || !data) return <NotFound />

  const { category, subcategories, articles } = data
  const parentSlug = category.parent?.slug || category.slug
  const isSubcategory = !!category.parent
  const basePath = isSubcategory
    ? `/cullector/${parentSlug}/${category.slug}`
    : `/cullector/${category.slug}`
  const totalPages = Math.max(
    1,
    Math.ceil((data.articlesTotal || 0) / CATEGORY_PAGE_SIZE)
  )

  return (
    <PageShell
      title={`${category.name} Guides — Cullector`}
      description={category.introParagraph || undefined}
      activeSlug={parentSlug}
    >
      <JsonLdSchema
        schema={categoryJsonLd(category.name, basePath, category.parent)}
        keyOverride="cullector-category-jsonld"
      />
      <div className="page-header">
        <div className="wrap">
          <div className="breadcrumb">
            <a href="/cullector">Cullector</a>
            <span className="sep">/</span>
            {isSubcategory && category.parent && (
              <>
                <a href={`/cullector/${category.parent.slug}`}>
                  {category.parent.name}
                </a>
                <span className="sep">/</span>
              </>
            )}
            <span className="current">{category.name}</span>
          </div>
          <h1 className="t-h1">{category.name}</h1>
          {category.introParagraph && (
            <p className="page-dek t-body">{category.introParagraph}</p>
          )}
          {subcategories.length > 0 && (
            <nav
              className="subcat-nav"
              aria-label={
                isSubcategory ? 'Related categories' : 'Subcategories'
              }
            >
              {subcategories.map(sub => (
                <a
                  key={sub.id}
                  href={`/cullector/${parentSlug}/${sub.slug}`}
                  className={
                    subcategorySlug === sub.slug ? 'active' : undefined
                  }
                >
                  {sub.name}
                </a>
              ))}
            </nav>
          )}
        </div>
      </div>

      <div className="grid-section">
        <div className="wrap">
          {articles.length > 0 ? (
            <div className="story-grid">
              {articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="hub-empty t-body">
              No guides here yet. Check back soon.
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} basePath={basePath} />
        )}
      </div>
    </PageShell>
  )
}

// ---------------------------------------------------------------------------
// /cullector/search?q=<term>[&page=N] — article search results. Same page-header +
// story-grid + pagination chrome as a category landing (the handoff has no separate
// results mock). The header search panel stays open, prefilled with the query. Not
// indexed: search result pages are thin/duplicate content for crawlers.
// ---------------------------------------------------------------------------

const SEARCH_PAGE_SIZE = 24
const SEARCH_QUERY_MIN_LENGTH = 2

interface SearchResultsData {
  q: string
  articles: ArticleCardData[]
  articlesTotal: number
}

const SearchResults: React.FC<{ path?: string }> = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const q = (params.get('q') || '').replace(/\s+/g, ' ').trim()
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1)
  const tooShort = q.length < SEARCH_QUERY_MIN_LENGTH

  const [data, setData] = useState<SearchResultsData | null>(null)
  const [loading, setLoading] = useState(!tooShort)
  const [error, setError] = useState(false)
  const hasLoadedOnce = useRef(false)

  useEffect(() => {
    if (tooShort) {
      setData(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(false)
    axios
      .get(`${baseURL}/api/v1/content-pages/public/search`, {
        params: {
          q,
          limit: SEARCH_PAGE_SIZE,
          offset: (page - 1) * SEARCH_PAGE_SIZE,
        },
      })
      .then(res => {
        if (cancelled) return
        setData(res.data?.data || null)
        if (hasLoadedOnce.current) window.scrollTo(0, 0)
        hasLoadedOnce.current = true
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [q, page, tooShort])

  const total = data?.articlesTotal || 0
  const articles = data?.articles || []
  const totalPages = Math.max(1, Math.ceil(total / SEARCH_PAGE_SIZE))
  const searchHref = (n: number) => {
    const next = new URLSearchParams({ q })
    if (n > 1) next.set('page', String(n))
    return `${CULLECTOR_SEARCH_PATH}?${next.toString()}`
  }

  const dek = tooShort
    ? 'Type at least two characters to search the guides.'
    : loading
    ? 'Searching…'
    : error
    ? 'Something went wrong. Please try again.'
    : total === 0
    ? `No guides match “${q}”. Try a different word, or browse by category below.`
    : `${total} ${total === 1 ? 'guide' : 'guides'} matching “${q}”${
        totalPages > 1 ? ` · page ${page} of ${totalPages}` : ''
      }`

  return (
    <PageShell
      title={q ? `“${q}” — Search — Cullector` : 'Search — Cullector'}
      searchQuery={q}
      noindex
    >
      <div className="page-header">
        <div className="wrap">
          <div className="breadcrumb">
            <a href="/cullector">Cullector</a>
            <span className="sep">/</span>
            <span className="current">Search</span>
          </div>
          <h1 className="t-h1">{q ? `Results for “${q}”` : 'Search guides'}</h1>
          <p className="page-dek t-body" role="status" aria-live="polite">
            {dek}
          </p>
        </div>
      </div>

      <div className="grid-section">
        <div className="wrap">
          {loading ? (
            <Loading />
          ) : articles.length > 0 ? (
            <div className="story-grid">
              {articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="hub-empty t-body">
              {tooShort
                ? 'Use the search box above to find a guide.'
                : error
                ? 'We couldn’t load results right now.'
                : 'Nothing found yet — our guides cover fashion, beauty, home, tech, and wellness.'}
            </div>
          )}
        </div>
        {!loading && !error && totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            basePath={CULLECTOR_SEARCH_PATH}
            pageHref={searchHref}
          />
        )}
      </div>
    </PageShell>
  )
}

// ---------------------------------------------------------------------------
// /cullector/<slug> is ambiguous: it's a category landing if the slug matches an
// active editorial category (categories win collisions — a small curated set),
// otherwise an article. Resolved against the cached active-categories list that
// the header fetches anyway; if that fetch fails, probe the article endpoint first.
// ---------------------------------------------------------------------------

const SlugResolver: React.FC<{ path?: string; slug?: string }> = ({ slug }) => {
  const [mode, setMode] = useState<'loading' | 'category' | 'article'>(
    'loading'
  )

  useEffect(() => {
    let cancelled = false
    if (!slug) return undefined
    setMode('loading')

    getActiveEditorialCategories()
      .then(categories => {
        if (cancelled) return
        const allSlugs = categories.flatMap(c => [
          c.slug,
          ...(c.children || []).map(s => s.slug),
        ])
        setMode(allSlugs.includes(slug) ? 'category' : 'article')
      })
      .catch(async () => {
        try {
          await axios.get(`${baseURL}/api/v1/content-pages/public/${slug}`)
          if (!cancelled) setMode('article')
        } catch {
          if (!cancelled) setMode('category')
        }
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  if (mode === 'loading') {
    return (
      <PageShell title="Cullector — The Best, Curated">
        <Loading />
      </PageShell>
    )
  }
  if (mode === 'category') return <CategoryLanding categorySlug={slug} />
  return <CullectorContentPage slug={slug} />
}

// Private draft preview at /cullector/preview/<token> — the article component in
// preview mode (fetch by share token, banner, noindex).
const CullectorPreview: React.FC<{ path?: string; token?: string }> = ({
  token,
}) => <CullectorContentPage previewToken={token} />

const CullectorPage = () => {
  return (
    <Router basepath="/cullector">
      <CullectorHome path="/" />
      {/* Static segment outranks /:slug, so a category slugged "search" would be shadowed. */}
      <SearchResults path="/search" />
      <SlugResolver path="/:slug" />
      {/* Static segment outranks the dynamic two-segment category route below. */}
      <CullectorPreview path="/preview/:token" />
      <CategoryLanding path="/:categorySlug/:subcategorySlug" />
    </Router>
  )
}

export default CullectorPage
